// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * cm-coding: The Content navigation describes resources owned by the selected
 * workspace, so this tab lists only that workspace's knowledge bases just as
 * its neighboring Schemas and Datasets tabs do. Mixing other workspaces into
 * this list makes administrators think those resources belong to the selected
 * workspace. Cross-workspace work remains explicit: cloning can copy a
 * knowledge base into another workspace, which is
 * how application-template guidance is seeded into new workspaces.
 */
import { useEffect, useMemo, useState, type FC } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Eye, Plus, Settings, Trash2 } from 'lucide-react'

import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableCard,
  TableCardEmpty,
  TableCardLoading,
} from '@/components/ui'
import { toast } from 'sonner'
import { ConfirmDialog, Field, FormDialog } from '@/components/forms'
import { apiClient } from '@/app-shell/api/api-client'
import { fetchOrgWorkspaces, useAuth } from '@/subsystems/workspaces'
import { useOrganizations } from '@/subsystems/workspaces/hooks/use-organizations'
import { slugify } from '@/subsystems/layout/utils/layout-utils'
import {
  KnowledgeBaseSettingsForm,
  type KnowledgeBaseSettingsFormValues,
} from '@/subsystems/knowledge-base/shared/components/KnowledgeBaseSettingsForm'

export interface InstanceSummary {
  instanceId: string
  label: string | null
  description: string | null
  mode: 'findings' | 'report' | 'criteria'
  itemSchemaId: string | null
  injectionTemplate: string | null
  itemCount: number
  updatedAt: string | null
  updatedBy: string | null
}

const EMPTY_SETTINGS: KnowledgeBaseSettingsFormValues = {
  label: '',
  description: '',
  mode: 'findings',
  itemSchemaId: null,
  injectionTemplate: '',
}

const instancesQueryKey = (orgId: string, wsId: string) => [
  'workspace-kb-instances',
  orgId,
  wsId,
]

interface InstancesTabProps {
  onView: (target: { orgId: string; wsId: string; instanceId: string; label: string }) => void
}

export const InstancesTab: FC<InstancesTabProps> = ({ onView }) => {
  const queryClient = useQueryClient()
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth()
  const orgId = selectedOrganizationId ?? ''
  const wsId = selectedWorkspaceId ?? ''

  const [createOpen, setCreateOpen] = useState(false)
  const [cloneSource, setCloneSource] = useState<InstanceSummary | null>(null)
  const [editingSettings, setEditingSettings] = useState<InstanceSummary | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<InstanceSummary | null>(null)
  const [deleting, setDeleting] = useState(false)

  const { data: instances = [], isLoading } = useQuery<InstanceSummary[]>({
    queryKey: instancesQueryKey(orgId, wsId),
    queryFn: async () => {
      const { data } = await apiClient.get<InstanceSummary[]>(
        `/org/${orgId}/ws/${wsId}/knowledge_base/instances`,
      )
      return data ?? []
    },
    enabled: !!orgId && !!wsId,
    staleTime: 60_000,
  })

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: instancesQueryKey(orgId, wsId) })

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await apiClient.delete(
        `/org/${orgId}/ws/${wsId}/knowledge_base/instances/${deleteTarget.instanceId}`,
      )
      setDeleteTarget(null)
      refresh()
      toast.success('Knowledge base deleted')
    } catch (err) {
      toast.error('Failed to delete', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setDeleting(false)
    }
  }

  if (!orgId || !wsId) {
    return <div className="text-sm text-slate-500">Select a workspace to view knowledge bases.</div>
  }

  const newButton = (
    <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="kb-new">
      <Plus className="h-4 w-4 mr-1" />
      New Knowledge Base
    </Button>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">{newButton}</div>

      <TableCard
        title="Workspace knowledge bases"
        meta={
          instances.length > 0
            ? `${instances.length} knowledge base${instances.length === 1 ? '' : 's'}`
            : undefined
        }
      >
        {isLoading ? (
          <TableCardLoading label="Loading knowledge bases…" />
        ) : instances.length === 0 ? (
          <TableCardEmpty
            title="No knowledge bases yet"
            description="Create one to give this workspace's analyses their guidance library."
            action={newButton}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Schema</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Instance ID</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {instances.map(inst => (
                <TableRow
                  key={inst.instanceId}
                  data-testid={`kb-instance-row-${inst.instanceId}`}
                >
                  <TableCell className="font-[550]">{inst.label || inst.instanceId}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs uppercase">
                      {inst.mode}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">
                    {inst.itemSchemaId ?? '—'}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 tabular-nums">
                    {inst.itemCount}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">
                    {inst.instanceId}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                          onView({
                            orgId,
                            wsId,
                            instanceId: inst.instanceId,
                            label: inst.label || inst.instanceId,
                          })
                        }
                        title="View items"
                        aria-label="View knowledge base items"
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setEditingSettings(inst)}
                        title="Settings"
                        aria-label="Knowledge base settings"
                      >
                        <Settings className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setCloneSource(inst)}
                        title="Clone"
                        aria-label="Clone knowledge base"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => setDeleteTarget(inst)}
                        title="Delete"
                        aria-label={`Delete ${inst.label || inst.instanceId}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableCard>

      <CreateKbDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        orgId={orgId}
        wsId={wsId}
        onCreated={() => {
          refresh()
          toast.success('Knowledge base created')
        }}
      />
      <CloneKbDialog
        source={cloneSource}
        sourceOrgId={orgId}
        sourceWsId={wsId}
        onOpenChange={open => !open && setCloneSource(null)}
        onCloned={() => {
          refresh()
          toast.success('Knowledge base cloned')
        }}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={open => !open && !deleting && setDeleteTarget(null)}
        title="Delete knowledge base?"
        description={
          deleteTarget
            ? `Permanently delete "${deleteTarget.label || deleteTarget.instanceId}", including all of its items. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="destructive"
        loading={deleting}
        onConfirm={handleDelete}
      />
      <EditSettingsDialog
        target={editingSettings}
        orgId={orgId}
        wsId={wsId}
        onOpenChange={open => !open && setEditingSettings(null)}
        onSaved={() => {
          refresh()
          setEditingSettings(null)
          toast.success('Settings updated')
        }}
      />
    </div>
  )
}

interface EditSettingsDialogProps {
  target: InstanceSummary | null
  orgId: string
  wsId: string
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}

const EditSettingsDialog: FC<EditSettingsDialogProps> = ({
  target,
  orgId,
  wsId,
  onOpenChange,
  onSaved,
}) => {
  const [values, setValues] = useState<KnowledgeBaseSettingsFormValues>(EMPTY_SETTINGS)
  const [submitting, setSubmitting] = useState(false)
  const open = !!target

  useEffect(() => {
    if (!target) return
    setValues({
      label: target.label ?? '',
      description: target.description ?? '',
      mode: target.mode,
      itemSchemaId: target.itemSchemaId,
      injectionTemplate: target.injectionTemplate ?? '',
    })
  }, [target])

  const canSubmit = !submitting && values.label.trim().length > 0

  const handleSubmit = async () => {
    if (!target || !canSubmit) return
    setSubmitting(true)
    try {
      await apiClient.put(
        `/org/${orgId}/ws/${wsId}/knowledge_base/instances/${target.instanceId}/settings`,
        {
          label: values.label.trim(),
          description: values.description.trim() || null,
          mode: values.mode,
          itemSchemaId: values.itemSchemaId,
          injectionTemplate: values.injectionTemplate.trim() || null,
        },
      )
      onOpenChange(false)
      onSaved()
    } catch (err) {
      toast.error('Failed to update settings', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Knowledge Base Settings"
      onSubmit={handleSubmit}
      submitting={submitting}
      canSubmit={canSubmit}
      submitLabel="Save"
      size="lg"
    >
      {target && (
        <>
          <div className="rounded-md border border-border p-3 text-sm">
            <p className="font-medium">{target.label || target.instanceId}</p>
            <p className="text-xs text-muted-foreground font-mono">
              {orgId} / {wsId} / {target.instanceId}
            </p>
          </div>
          <KnowledgeBaseSettingsForm
            values={values}
            onChange={setValues}
            disabled={submitting}
            labelInputId="edit-kb-label"
          />
        </>
      )}
    </FormDialog>
  )
}

interface OrgWsPickerProps {
  orgId: string
  wsId: string
  onChange: (orgId: string, wsId: string) => void
  disabled?: boolean
}

const OrgWsPicker: FC<OrgWsPickerProps> = ({ orgId, wsId, onChange, disabled }) => {
  const { data: orgs = [] } = useOrganizations()
  const { data: workspaces = [] } = useQuery({
    queryKey: ['admin-kb-workspaces', orgId],
    queryFn: () => fetchOrgWorkspaces(orgId),
    enabled: !!orgId,
    staleTime: 60_000,
  })

  return (
    <div className="grid grid-cols-2 gap-2 min-w-0">
      <Field label="Organization">
        <Select value={orgId} onValueChange={value => onChange(value, '')} disabled={disabled}>
          <SelectTrigger className="w-full min-w-0">
            <SelectValue placeholder="Select organization" className="truncate" />
          </SelectTrigger>
          <SelectContent>
            {orgs.map(o => (
              <SelectItem key={o.orgId} value={o.orgId}>
                {o.name || o.orgId}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Workspace">
        <Select value={wsId} onValueChange={value => onChange(orgId, value)} disabled={disabled || !orgId}>
          <SelectTrigger className="w-full min-w-0">
            <SelectValue placeholder="Select workspace" className="truncate" />
          </SelectTrigger>
          <SelectContent>
            {workspaces.map(w => (
              <SelectItem key={w.wsId} value={w.wsId}>
                {w.name || w.wsId}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </div>
  )
}

interface CreateKbDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
  wsId: string
  onCreated: () => void
}

const CreateKbDialog: FC<CreateKbDialogProps> = ({ open, onOpenChange, orgId, wsId, onCreated }) => {
  const [values, setValues] = useState<KnowledgeBaseSettingsFormValues>(EMPTY_SETTINGS)
  const [submitting, setSubmitting] = useState(false)

  const handleOpenChange = (next: boolean) => {
    if (!next) setValues(EMPTY_SETTINGS)
    onOpenChange(next)
  }

  const canSubmit = values.label.trim().length > 0 && !submitting

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      const trimmed = values.label.trim()
      const instanceId = `${slugify(trimmed)}-${crypto.randomUUID().slice(0, 8)}`
      await apiClient.post('/admin/knowledge_base/instances', {
        orgId,
        wsId,
        instanceId,
        label: trimmed,
        description: values.description.trim() || null,
        mode: values.mode,
        itemSchemaId: values.itemSchemaId,
        injectionTemplate: values.injectionTemplate.trim() || null,
      })
      handleOpenChange(false)
      onCreated()
    } catch (err) {
      toast.error('Failed to create', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="New Knowledge Base"
      onSubmit={handleSubmit}
      submitting={submitting}
      canSubmit={canSubmit}
      submitLabel="Create"
      size="2xl"
    >
      <KnowledgeBaseSettingsForm
        values={values}
        onChange={setValues}
        disabled={submitting}
        labelInputId="new-kb-label"
      />
    </FormDialog>
  )
}

interface CloneKbDialogProps {
  source: InstanceSummary | null
  sourceOrgId: string
  sourceWsId: string
  onOpenChange: (open: boolean) => void
  onCloned: () => void
}

const CloneKbDialog: FC<CloneKbDialogProps> = ({
  source,
  sourceOrgId,
  sourceWsId,
  onOpenChange,
  onCloned,
}) => {
  const [targetOrgId, setTargetOrgId] = useState('')
  const [targetWsId, setTargetWsId] = useState('')
  const [targetLabel, setTargetLabel] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const sourceLabel = source?.label || source?.instanceId || ''
  const initialLabel = useMemo(() => (sourceLabel ? `${sourceLabel} (copy)` : ''), [sourceLabel])

  const open = !!source

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setTargetOrgId('')
      setTargetWsId('')
      setTargetLabel('')
    } else {
      setTargetLabel(initialLabel)
    }
    onOpenChange(next)
  }

  const canSubmit =
    !!source && !!targetOrgId && !!targetWsId && targetLabel.trim().length > 0 && !submitting

  const handleSubmit = async () => {
    if (!canSubmit || !source) return
    setSubmitting(true)
    try {
      const trimmed = targetLabel.trim()
      const targetInstanceId = `${slugify(trimmed)}-${crypto.randomUUID().slice(0, 8)}`
      await apiClient.post('/admin/knowledge_base/clone', {
        sourceOrgId,
        sourceWsId,
        sourceInstanceId: source.instanceId,
        targetOrgId,
        targetWsId,
        targetInstanceId,
        targetLabel: trimmed,
      })
      handleOpenChange(false)
      onCloned()
    } catch (err) {
      toast.error('Failed to clone', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Clone Knowledge Base"
      onSubmit={handleSubmit}
      submitting={submitting}
      canSubmit={canSubmit}
      submitLabel="Clone"
      size="2xl"
    >
      {source && (
        <>
          <div className="rounded-md border border-border p-3 text-sm">
            <p className="font-medium">{sourceLabel}</p>
            <p className="text-xs text-muted-foreground font-mono">
              from {sourceOrgId} / {sourceWsId} / {source.instanceId}
            </p>
          </div>
          <Field label="Target workspace">
            <OrgWsPicker
              orgId={targetOrgId}
              wsId={targetWsId}
              onChange={(o, w) => {
                setTargetOrgId(o)
                setTargetWsId(w)
              }}
              disabled={submitting}
            />
          </Field>
          <Field label="New name" htmlFor="clone-kb-label">
            <Input
              id="clone-kb-label"
              value={targetLabel}
              onChange={e => setTargetLabel(e.target.value)}
              placeholder="Knowledge base name"
            />
          </Field>
        </>
      )}
    </FormDialog>
  )
}
